import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Filler,
  Title,
} from 'chart.js';
import API_BASE_URL from '../config/api';
import { useTranslation } from '../i18n';

ChartJS.register(
  LineElement, PointElement, CategoryScale, LinearScale,
  Tooltip, Legend, Filler, Title
);

const ElevationChart = ({ fileId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/fit/parsed-data/${fileId}`);
        setData(res.data);
      } catch (err) {
        console.error('Error al cargar datos:', err);
      }
    };
    if (fileId) fetchData();
  }, [fileId]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: {
        position: 'top',
        labels: { font: { size: 9 }, boxWidth: 10, padding: 6 },
      },
      title: { display: false },
      tooltip: { mode: 'index', intersect: false },
    },
    scales: {
      x: {
        title: { display: true, text: t('elevationProfile.axisLap'), font: { size: 9 } },
        ticks: { font: { size: 8 } },
      },
      altitude: {
        position: 'left',
        title: { display: true, text: t('elevationProfile.axisAltitude'), font: { size: 8 } },
        ticks: { font: { size: 8 } },
      },
      pace: {
        position: 'right',
        title: { display: true, text: t('elevationProfile.axisPaceMinKm'), font: { size: 8 } },
        ticks: { font: { size: 8 } },
        grid: { drawOnChartArea: false },
      },
      heartRate: {
        position: 'right',
        title: { display: true, text: t('elevationProfile.axisBpm'), font: { size: 8 } },
        ticks: { font: { size: 8 } },
        grid: { drawOnChartArea: false },
      },
      cadence: {
        position: 'right',
        title: { display: true, text: t('elevationProfile.axisCadence'), font: { size: 8 } },
        ticks: { font: { size: 8 } },
        grid: { drawOnChartArea: false },
      },
      temperature: {
        position: 'right',
        title: { display: true, text: t('elevationProfile.axisTemp'), font: { size: 8 } },
        ticks: { font: { size: 8 } },
        grid: { drawOnChartArea: false },
        display: false,
      },
    },
  }), [t]);

  if (!data?.laps) return <div>{t('elevationProfile.loading')}</div>;

  const laps = data.laps;
  const labels = laps.map((_, i) => i + 1);

  const calculatePace = (time, distance) => {
    if (!time || !distance) return null;
    const pace = (time / distance) * 16.6667;
    return isFinite(pace) ? parseFloat(pace.toFixed(2)) : null;
  };

  const getMetric = (key) => {
    if (key === 'avg_pace') {
      return laps.map(lap => calculatePace(lap.total_timer_time, lap.total_distance));
    }
    if (key === 'altitude') {
      const direct = laps.map(lap =>
        lap.enhanced_avg_altitude ?? lap.avg_altitude ?? lap.start_altitude ?? null
      );
      if (direct.some(v => v != null)) return direct;

      const records = data.records || [];
      if (records.length) {
        let cumDist = 0;
        return laps.map(lap => {
          const lapStart = cumDist;
          cumDist += lap.total_distance ?? 0;
          const lapEnd = cumDist;

          const altValues = records
            .filter(r => (r.distance ?? 0) >= lapStart && (r.distance ?? 0) <= lapEnd)
            .map(r => r.enhanced_altitude ?? r.altitude)
            .filter(v => v != null && Number.isFinite(v));

          if (!altValues.length) return null;
          return parseFloat(
            (altValues.reduce((s, v) => s + v, 0) / altValues.length).toFixed(1)
          );
        });
      }

      return laps.map(() => null);
    }
    return laps.map(lap => lap[key] ?? null);
  };

  const chartData = {
    labels,
    datasets: [
      {
        label: t('elevationProfile.seriesAltitudeImproved'),
        data: getMetric('altitude'),
        borderColor: 'rgba(0,100,0,0.9)',
        backgroundColor: 'rgba(0,100,0,0.08)',
        yAxisID: 'altitude',
        fill: true,
        tension: 0.4,
        pointRadius: 2,
        borderWidth: 2,
      },
      {
        label: t('elevationProfile.seriesPaceAvg'),
        data: getMetric('avg_pace'),
        borderColor: 'rgba(0,191,255,0.9)',
        backgroundColor: 'rgba(0,191,255,0.08)',
        yAxisID: 'pace',
        fill: true,
        tension: 0.4,
        pointRadius: 2,
        borderWidth: 1.5,
      },
      {
        label: t('elevationProfile.seriesHr'),
        data: getMetric('avg_heart_rate'),
        borderColor: 'rgba(255,99,132,0.9)',
        backgroundColor: 'rgba(255,99,132,0.05)',
        yAxisID: 'heartRate',
        fill: false,
        tension: 0.4,
        pointRadius: 2,
        borderWidth: 1.5,
      },
      {
        label: t('elevationProfile.seriesCadence'),
        data: getMetric('avg_cadence'),
        borderColor: 'rgba(234,179,8,0.9)',
        backgroundColor: 'rgba(234,179,8,0.05)',
        yAxisID: 'cadence',
        fill: false,
        tension: 0.4,
        pointRadius: 1,
        borderWidth: 1.5,
      },
      {
        label: t('elevationProfile.seriesTemp'),
        data: getMetric('avg_temperature'),
        borderColor: 'rgba(148,163,184,0.9)',
        backgroundColor: 'rgba(148,163,184,0.05)',
        yAxisID: 'temperature',
        fill: false,
        tension: 0.4,
        pointRadius: 1,
        borderWidth: 1.5,
        hidden: true,
      },
    ],
  };

  return <Line data={chartData} options={chartOptions} />;
};

export default ElevationChart;
